import { DashboardTile } from "@/components/common/DashboardTile";
import { DASHBOARD_GROUPS, filterVisibleGroups } from "@/lib/dashboard";
import type { DashboardGroup } from "@/lib/dashboard";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

function DashboardGroupPanel({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-col rounded-2xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)]/60 p-3 dark:border-white/10 dark:bg-slate-900/40 lg:min-h-[11rem] lg:p-4",
        className
      )}
    >
      <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--lm-muted)] dark:text-slate-500 lg:mb-4 lg:text-xs">
        {label}
      </p>
      {children}
    </div>
  );
}

function GroupTiles({ group }: { group: DashboardGroup }) {
  const singleTile = group.tiles.length === 1;
  return (
    <div
      className={cn(
        "grid min-h-0 w-full flex-1 gap-3",
        singleTile
          ? "grid-cols-1"
          : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2"
      )}
    >
      {group.tiles.map((tile) => (
        <DashboardTile
          key={tile.id}
          tile={tile}
          stacked={singleTile}
          className={singleTile ? "h-full min-h-0" : undefined}
        />
      ))}
    </div>
  );
}

export function AppDashboard() {
  const { hasPermission } = useAuth();
  const visibleGroups = filterVisibleGroups(DASHBOARD_GROUPS, hasPermission);

  const assistantGroup = visibleGroups.find((g) => g.id === "assistant");
  const researchGroup = visibleGroups.find((g) => g.id === "research-agents");
  const contractingGroup = visibleGroups.find((g) => g.id === "contracting-agents");
  const documentGroup = visibleGroups.find((g) => g.id === "document-management");

  const researchOnly =
    Boolean(researchGroup) &&
    !contractingGroup &&
    !documentGroup &&
    !assistantGroup;

  const useLgThreeColumnGrid = !researchOnly && visibleGroups.length > 0;

  const row1HasAgentGroups = Boolean(researchGroup) || Boolean(contractingGroup);

  function assistantPanelClassName(): string | undefined {
    if (!useLgThreeColumnGrid) return undefined;
    if (documentGroup && row1HasAgentGroups) {
      return "lg:row-start-2 lg:col-start-3";
    }
    if (documentGroup) {
      return "lg:row-start-1 lg:col-start-3";
    }
    if (row1HasAgentGroups) {
      return "lg:row-start-2 lg:col-start-1 lg:col-span-3";
    }
    return "lg:row-start-1 lg:col-start-1 lg:col-span-3";
  }

  return (
    <section className="flex flex-1 flex-col gap-4 p-4 md:gap-5 md:p-6 lg:gap-6 lg:p-8">
      {visibleGroups.length > 0 ? (
        <div
          className={cn(
            "grid grid-cols-1 gap-4 lg:items-stretch lg:gap-4 xl:gap-5",
            useLgThreeColumnGrid
              ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1.4fr)] lg:grid-rows-[auto_auto]"
              : "lg:grid-cols-1"
          )}
        >
          {assistantGroup ? (
            <DashboardGroupPanel
              label={assistantGroup.label}
              className={assistantPanelClassName()}
            >
              <GroupTiles group={assistantGroup} />
            </DashboardGroupPanel>
          ) : null}

          {researchGroup ? (
            <DashboardGroupPanel
              label={researchGroup.label}
              className={
                useLgThreeColumnGrid
                  ? "lg:row-start-1 lg:col-start-1"
                  : undefined
              }
            >
              <GroupTiles group={researchGroup} />
            </DashboardGroupPanel>
          ) : null}

          {contractingGroup ? (
            <DashboardGroupPanel
              label={contractingGroup.label}
              className={
                useLgThreeColumnGrid
                  ? researchGroup
                    ? "lg:row-start-1 lg:col-start-2 lg:col-span-2"
                    : "lg:row-start-1 lg:col-start-1 lg:col-span-3"
                  : undefined
              }
            >
              <GroupTiles group={contractingGroup} />
            </DashboardGroupPanel>
          ) : null}

          {documentGroup ? (
            <DashboardGroupPanel
              label={documentGroup.label}
              className={
                useLgThreeColumnGrid
                  ? row1HasAgentGroups
                    ? "lg:row-start-2 lg:col-start-1 lg:col-span-2"
                    : assistantGroup
                      ? "lg:row-start-1 lg:col-start-1 lg:col-span-2"
                      : "lg:row-start-1 lg:col-start-1 lg:col-span-3"
                  : undefined
              }
            >
              <GroupTiles group={documentGroup} />
            </DashboardGroupPanel>
          ) : null}
        </div>
      ) : null}

      {visibleGroups.length === 0 ? (
        <p className="text-sm text-[var(--lm-muted)] dark:text-slate-400">
          No applications are enabled for your role.
        </p>
      ) : null}
    </section>
  );
}
